"use client";

import { Capacitor } from "@capacitor/core";
import { BellRing, Scale } from "lucide-react";
import { useEffect, useState } from "react";
import { formatEventDate } from "@/lib/dates/event-display";
import { appStoreUrl, PLAY_STORE_URL } from "@/lib/seo";

type SideStatus = "open" | "waitlist" | "full";

export type PassBalance = {
  mode: "balanced" | "open_to_all" | "late_fill";
  viewerSide: "male" | "female" | "other" | null;
  male: SideStatus;
  female: SideStatus;
  /** Seats each side can still take (null = no limit). Never displayed. */
  open: { male: number | null; female: number | null };
  /** "cap": shares are fixed. "pace": a side's limit grows as the other books. */
  style: "cap" | "pace";
};

const PLURAL = { male: "men", female: "women" } as const;

/**
 * One line under a balanced ticket. Says what the viewer can do, never the
 * numbers behind it: the host asked for a 1:1 room, not a live scoreboard.
 */
export function BalancedTicketNote({ balance }: { balance: PassBalance }) {
  const line = (() => {
    if (balance.mode === "late_fill") {
      return "Balanced crowd. In the final 24 hours, any remaining spots are open to everyone.";
    }
    if (balance.mode === "open_to_all") {
      return "Balanced crowd. The host has opened the remaining spots to everyone.";
    }
    const side = balance.viewerSide;
    if (side === "male" || side === "female") {
      if (balance[side] === "waitlist") {
        const other = PLURAL[side === "male" ? "female" : "male"];
        // Only "keep it level" opens more as the other side books; a capped
        // side waits for someone to cancel.
        return balance.style === "pace"
          ? `To keep the crowd balanced, spots for ${PLURAL[side]} are on a waitlist right now. More open as ${other} book.`
          : `To keep the crowd balanced, spots for ${PLURAL[side]} are all taken right now. Join the waitlist for the next one that opens.`;
      }
      return "Balanced crowd: spots are split between men and women.";
    }
    if (side === "other") {
      return "Balanced crowd: spots are split between men and women, so this ticket is only for people whose profile says man or woman.";
    }
    return "Balanced crowd: spots are split between men and women, so we'll ask for your gender. If your side is full, you'll join its waitlist instead.";
  })();

  return (
    <p className="col-span-2 flex items-start gap-2 text-xs leading-relaxed text-text-secondary">
      <Scale aria-hidden className="mt-px size-3.5 shrink-0" />
      <span>{line}</span>
    </p>
  );
}

/**
 * "You're #4 in line" and what happens when a spot opens, per mode. When a
 * spot is open right now the ticket row shows "Add" like for anyone else, so
 * this says so and keeps "Leave waitlist" as a quiet link.
 */
export function WaitlistPlaceNote({
  position,
  mode,
  side,
  spotOpen = false,
  onLeave,
  leaving = false,
}: {
  position: number | null;
  mode: "notify" | "hold";
  side: PassBalance["viewerSide"];
  spotOpen?: boolean;
  onLeave?: () => void;
  leaving?: boolean;
}) {
  const lane =
    side === "male" || side === "female" ? ` for ${PLURAL[side]}` : "";
  const place =
    position && position > 0
      ? `You're #${position} in line${lane}.`
      : `You're on the waitlist${lane}.`;
  // Position matters in both modes: alerts and holds go out front-first.
  const next = spotOpen
    ? "A spot is open right now. Add it and check out to take it; whoever books first gets it."
    : mode === "hold"
      ? "When a spot opens and you're next, we'll hold it for you for 24 hours and let you know."
      : "When spots open, we notify people from the front of the line first. Whoever books first gets the spot. If someone beats you to it, you're still on the waitlist for the next one.";

  return (
    <div className="col-span-2 rounded-2xl bg-surface-alt px-3.5 py-3 text-xs leading-relaxed text-text-secondary dark:bg-glass-bg-04">
      <p className="font-semibold text-text-primary">{place}</p>
      <p className="mt-0.5">{next}</p>
      {onLeave ? (
        <button
          type="button"
          onClick={onLeave}
          disabled={leaving}
          className="mt-2 font-medium text-text-secondary underline underline-offset-2 hover:text-text-primary disabled:opacity-50"
        >
          Leave waitlist
        </button>
      ) : null}
    </div>
  );
}

/** Shown when a head start is keeping just-freed spots for people in line. */
export function WaitlistHeadStartNote({ until }: { until: string }) {
  return (
    <p className="col-span-2 text-xs leading-relaxed text-text-secondary">
      Spots that just opened go to the waitlist first, until{" "}
      {formatEventDate(until, "h:mm a")}. Join the waitlist for a shot at one,
      or check back then.
    </p>
  );
}

/**
 * On the web, alerts arrive by email. The app delivers them the second a spot
 * opens, which matters when the first person to book gets it. Native shells
 * render nothing here; the push nudge covers them.
 */
export function WaitlistAppCard() {
  const [isWeb, setIsWeb] = useState(false);
  useEffect(() => setIsWeb(!Capacitor.isNativePlatform()), []);
  if (!isWeb) return null;

  const iosUrl = appStoreUrl();
  const links = [
    ...(iosUrl ? [{ label: "App Store", href: iosUrl }] : []),
    { label: "Google Play", href: PLAY_STORE_URL },
  ];

  return (
    <div className="mt-3 flex flex-col gap-3 rounded-[22px] border border-border bg-surface px-4 py-4 sm:flex-row sm:items-center sm:px-6 dark:border-glass-border-10">
      <BellRing aria-hidden className="size-5 shrink-0 text-text-secondary" />
      <div className="min-w-0 flex-1 text-sm">
        <p className="font-semibold text-text-primary">
          Hear about open spots instantly
        </p>
        <p className="text-text-secondary">
          We'll email you either way. With the Hiive app you also get a
          notification the moment a spot opens.
        </p>
      </div>
      <div className="flex shrink-0 gap-2">
        {links.map((link) => (
          <a
            key={link.label}
            href={link.href}
            target="_blank"
            rel="noreferrer"
            className="inline-flex h-9 items-center rounded-full border border-border px-3.5 text-xs font-semibold text-text-primary transition-colors hover:bg-surface-alt dark:border-white/35 dark:hover:bg-glass-bg-04"
          >
            {link.label}
          </a>
        ))}
      </div>
    </div>
  );
}
